import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/api/client';
import {
  assignReplacedAsset,
  listReplaceableAssets,
  removeReplacedAsset,
} from '@/api/hardwareRequests';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDate } from '@/lib/dateFormat';
import type {
  HardwareRequestDetail,
  HardwareRequestReplacedAsset,
  ReplaceableAsset,
} from '@/types/oscar';

/**
 * The lifecycle state the form moves an asset to when somebody names it as the
 * one being replaced - and the one an approver's pick moves it to too.
 */
const REPLACEMENT_REQUESTED = 'Replacement Requested';

/** Declined means nothing is being given, so nothing is being replaced. */
function canNameADevice(request: Pick<HardwareRequestDetail, 'RequestType' | 'ApprovalStatus'>) {
  return request.RequestType === 'Replacement' && request.ApprovalStatus !== 'Declined';
}

/**
 * Name the device being replaced (oscar migration 122).
 *
 * <p>Anthony, 14/10/2026: <i>"a user has not selected their device on the form,
 * we know it relates to their device - so the approver needs to select it -
 * create an option for it to be reopened and the approver assign."</i></p>
 *
 * ⛔ THE REQUESTER'S ANSWER IS NOT OVERWRITTEN. "Other - it is not listed, or I
 * would rather not say" stays on the request exactly as given; what an approver
 * picks sits BESIDE it, labelled, on the same card as a requester's would be.
 *
 * ⛔ APPROVERS ONLY - the same people who can decide, and reading the list is
 * gated too: what a named person holds is not for everyone who can open the
 * page. Helpdesk sees the evidence and nothing to press.
 *
 * ⚠ ONLY A REPLACEMENT THAT IS STILL LIVE. A new request replaces nothing, and a
 * declined one has ended - assigning would move an asset to Replacement Requested
 * for a request nobody is acting on. The server refuses both too.
 */
export function AssignReplacedAsset({
  request,
  hasAssets,
}: {
  request: HardwareRequestDetail;
  hasAssets: boolean;
}) {
  const { canApproveHardware } = useAuth();
  const queryClient = useQueryClient();
  const submissionId = request.SubmissionId;

  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // ⚠ ASKED ONLY WHEN THE DIALOG IS OPEN. A list of what somebody holds is read
  // when an approver reaches for it, not every time the page loads.
  const candidates = useQuery({
    queryKey: ['hardware-request', submissionId, 'replaceable-assets'],
    queryFn: ({ signal }) => listReplaceableAssets(submissionId, signal),
    enabled: open,
  });

  const assign = useMutation({
    // ⛔ ONE AT A TIME, in the order ticked. Two lifecycle writes racing each
    // other on the same request is the kind of thing that is fine until it is
    // not, and a stop at the first refusal says which asset was the problem.
    mutationFn: async () => {
      for (const assetId of picked) {
        await assignReplacedAsset(submissionId, assetId);
      }
    },
    onSuccess: () => {
      setPicked(new Set());
      setError(null);
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['hardware-request', submissionId] });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Could not assign the device.');
      // Whatever landed before the refusal is on the request now; refresh so
      // the list stops offering it.
      void queryClient.invalidateQueries({ queryKey: ['hardware-request', submissionId] });
    },
  });

  if (!canApproveHardware || !canNameADevice(request)) return null;

  const toggle = (assetId: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });

  const data = candidates.data;
  const forPlace = data?.HolderKind === 'Location';

  return (
    <div className="mt-3">
      {!hasAssets && (
        <p className="mb-2 text-sm text-darkGrey/80">
          The requester did not name a device. If you know which one it is, assign it here — their
          answer stays on the request as they gave it.
        </p>
      )}

      <button
        type="button"
        onClick={() => {
          setPicked(new Set());
          setError(null);
          setOpen(true);
        }}
        className="rounded border border-navy bg-white px-3 py-1.5 text-sm font-semibold text-navy hover:bg-bg"
      >
        {hasAssets ? 'Name another device' : 'Assign the device'}
      </button>

      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setError(null);
        }}
        title="Which device is being replaced?"
        description={
          <>
            Choose what {forPlace ? 'this location' : (data?.HolderName ?? 'they')} holds in Oscar.
            It is marked <strong>{REPLACEMENT_REQUESTED}</strong>, as it would have been if the
            requester had named it.
          </>
        }
        confirmLabel={assign.isPending ? 'Assigning…' : 'Assign'}
        confirmDisabled={picked.size === 0 || assign.isPending}
        closeOnConfirm={false}
        onConfirm={() => assign.mutate()}
        wide
      >
        {candidates.isLoading && <p className="text-sm text-darkGrey/70">Loading what they hold…</p>}

        {candidates.isError && (
          <p role="alert" className="text-sm text-red">
            Could not read what they hold. If this keeps happening, contact Onsite IT Support.
          </p>
        )}

        {/* ⚠ UNRESOLVED IS AN ANSWER. An empty list would read as "they hold
            nothing", which is a different statement and a wrong one. */}
        {data && !data.Resolved && (
          <p className="flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey/80">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{data.Reason ?? 'Oscar could not tell who this request is for.'}</span>
          </p>
        )}

        {data?.Resolved && data.Assets.length === 0 && (
          <p className="text-sm text-darkGrey/80">
            Oscar holds no devices against {forPlace ? 'this location' : (data.HolderName ?? 'them')}.
          </p>
        )}

        {data?.Resolved && data.Assets.length > 0 && (
          <fieldset className="max-h-72 overflow-y-auto rounded border border-border">
            <legend className="sr-only">Devices held</legend>
            <ul className="divide-y divide-border">
              {data.Assets.map((a) => (
                <CandidateRow
                  key={a.AssetId}
                  asset={a}
                  checked={picked.has(a.AssetId)}
                  onToggle={() => toggle(a.AssetId)}
                />
              ))}
            </ul>
          </fieldset>
        )}

        {error && (
          <p role="alert" className="mt-3 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </AlertDialog>
    </div>
  );
}

function CandidateRow({
  asset: a,
  checked,
  onToggle,
}: {
  asset: ReplaceableAsset;
  checked: boolean;
  onToggle: () => void;
}) {
  const already = a.AlreadyOnRequest;
  // ⚠ Said on the row, because it changes what assigning does: an asset already
  // at Replacement Requested is not moved, and is not put back if it is taken off.
  const alreadyRequested = a.LifecycleState === REPLACEMENT_REQUESTED;

  return (
    <li className="px-3 py-2">
      <label className={`flex items-start gap-3 ${already ? 'opacity-60' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          className="mt-1"
          checked={checked}
          disabled={already}
          onChange={onToggle}
          aria-label={a.DisplayName}
        />
        <span className="text-sm text-darkGrey">
          <span className="font-semibold text-navy">{a.DisplayName}</span>
          <span className="block text-xs text-darkGrey/70">
            {[a.Category, a.LifecycleState].filter(Boolean).join(' · ')}
            {a.AssignedDate && <> · held since {formatDate(a.AssignedDate)}</>}
          </span>
          {already && (
            <span className="block text-xs font-semibold text-darkGrey/70">Already on this request</span>
          )}
          {!already && alreadyRequested && (
            <span className="block text-xs text-darkGrey/70">
              Already marked {REPLACEMENT_REQUESTED} — it will not be moved again.
            </span>
          )}
        </span>
      </label>
    </li>
  );
}

/**
 * Who named this device - and, for an approver's pick, the way to take it back.
 *
 * ⛔ ONLY AN APPROVER'S PICK CAN BE TAKEN BACK. What the requester named is
 * their answer and stays as they gave it; the control is not offered on it.
 *
 * ⚠ SAYS WHAT HAPPENS TO THE ASSET. Taking a pick back restores the state it
 * was in only when the assignment moved it and nothing has moved it since - and
 * the confirm says so rather than promising a clean undo.
 */
export function AssignedBy({
  asset,
  submissionId,
}: {
  asset: HardwareRequestReplacedAsset;
  submissionId: number;
}) {
  const { canApproveHardware } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const takeOff = useMutation({
    mutationFn: () => removeReplacedAsset(submissionId, asset.AssetId),
    onSuccess: () => {
      setError(null);
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['hardware-request', submissionId] });
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not take the device off this request.'),
  });

  if (asset.Source !== 'Approver') {
    return <p className="mt-2 text-xs text-darkGrey/70">Named by the requester.</p>;
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      <p className="text-xs text-darkGrey/70">
        Assigned by {asset.AssignedBy ?? 'an approver'}
        {asset.AssignedAtUtc && <> on {formatDate(asset.AssignedAtUtc)}</>} — the requester did not
        name it.
      </p>

      {canApproveHardware && (
        <>
          <button
            type="button"
            onClick={() => {
              setError(null);
              setOpen(true);
            }}
            className="text-xs font-semibold text-navy hover:underline"
          >
            Take off this request
          </button>

          <AlertDialog
            open={open}
            onOpenChange={setOpen}
            title="Take this device off the request?"
            description={
              <>
                It goes back to <strong>{asset.SnapshotPriorState ?? 'the state it was in'}</strong>{' '}
                if this assignment moved it and nothing has moved it since — otherwise it is left
                as it is. The requester&rsquo;s own answer is not affected.
              </>
            }
            confirmLabel={takeOff.isPending ? 'Taking off…' : 'Take it off'}
            confirmDisabled={takeOff.isPending}
            closeOnConfirm={false}
            onConfirm={() => takeOff.mutate()}
          >
            {error && (
              <p role="alert" className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
                {error}
              </p>
            )}
          </AlertDialog>
        </>
      )}
    </div>
  );
}
